import CurrenciesData from "~/data/currencies.json";
import type { DangerousGoods, DangerousGoodsTemplate, IOrderline } from "~/types/orderline";

const dangerousGoodsTemplates = ref<DropdownOption[]>([]);

export const useOrderlines = () => {
  const { t } = useI18n();
  const countries = getCountrySelectOptions();

  const units = [{ label: "g", value: "g" }];

  const currencies = CurrenciesData.map((c) => ({
    label: c.code,
    value: c.code,
  }));

  const generateOrderline = (): IOrderline => {
    return {
      qty: undefined,
      item_number: undefined,
      customs: [
        {
          description: undefined,
          total_price: undefined,
          currency_code: "DKK",
          sender_tariff: undefined,
          receiver_tariff: undefined,
          origin_country: undefined,
          weight: undefined,
          unit: "g",
        },
      ],
      dangerous_goods_set: false,
      LQ: false,
      dangerous_goods: [],
    };
  };

  const generateDangerousGoods = (): DangerousGoods => {
    return {
      data: [],
      environmentally_hazardous: false,
      hazard_class: undefined,
      limited_quantity: true,
      nos_description: undefined,
      packaging_description: undefined,
      packing_instruction: undefined,
      packaging_quantity: undefined,
      packing_group: undefined,
      proper_shipping_name: undefined,
      quantity_unit: "KG",
      quantity_value: undefined,
      tunnel_restriction_code: undefined,
      un_number: undefined,
    };
  };

  const returnSelecteditem = (key: string, type: string) => {
    switch (type) {
      case "unit":
        return units.find((u) => u.value === key)?.label;
      case "currency":
        return currencies.find((c) => c.value === key)?.label;
      case "country":
        return countries.find((c) => c.value === key)?.label;
    }
  };

  const orderLineMissingInfo = (orderLine: IOrderline, isCrossBorder: boolean) => {
    return !orderLine.qty || !orderLine.item_number || !orderLine.customs[0].description;
  };

  const formatOrderlineTitle = (orderline: IOrderline) => {
    const parts = [];

    if (orderline.qty) parts.push(`${orderline.qty}x`);
    if (orderline.item_number) parts.push(orderline.item_number);
    if (!orderline.customs || orderline.customs.length === 0) return parts.join(" ");
    if (orderline.customs[0].description) parts.push(orderline.customs[0].description);

    if (orderline.customs[0].weight) {
      parts.push(`${t("at")} ${orderline.customs[0].weight}g,`);
    }

    if (orderline.customs[0].total_price) {
      parts.push(
        `${t("to").toLowerCase()} ${orderline.customs[0].total_price} ${returnSelecteditem(orderline.customs[0].currency_code, "currency")}`
      );
    }

    if (orderline.customs[0].origin_country) {
      parts.push(`${t("from").toLowerCase()} ${returnSelecteditem(orderline.customs[0].origin_country, "country")}`);
    }

    return parts.join(" ");
  };

  const formatOrderlineTitleShort = (orderline: IOrderline) => {
    const parts = [];
    if (orderline.qty) parts.push(`${orderline.qty}x`);
    if (!orderline.customs || orderline.customs.length === 0) {
      if (orderline.item_number) parts.push(orderline.item_number);
      return parts.join(" ");
    }
    if (orderline.customs[0].description) parts.push(orderline.customs[0].description);
    if (orderline.item_number) parts.push(orderline.item_number);
    return parts.join(" ");
  };

  const formatOrderlineTitleConsignment = (orderline: IOrderline) => {
    const parts = [];

    if (orderline.item_number) parts.push(orderline.item_number);
    if (!orderline.customs || orderline.customs.length === 0) return parts.join(" ");
    if (orderline.customs[0].description) parts.push(orderline.customs[0].description);

    if (orderline.customs[0].weight) {
      parts.push(`${t("at")} ${orderline.customs[0].weight}g,`);
    }

    if (orderline.customs[0].total_price) {
      parts.push(
        `${t("to").toLowerCase()} ${orderline.customs[0].total_price} ${returnSelecteditem(orderline.customs[0].currency_code, "currency")}`
      );
    }

    if (orderline.customs[0].origin_country) {
      parts.push(`${t("from").toLowerCase()} ${returnSelecteditem(orderline.customs[0].origin_country, "country")}`);
    }

    return parts.join(" ");
  };

  const formatDangerousGoodsSummary = (dg: DangerousGoods) => {
    if (!dg) return "";

    const parts = [];

    if (dg.packaging_quantity) parts.push(`${dg.packaging_quantity}`);
    if (dg.packaging_description) parts.push(dg.packaging_description);
    if (dg.un_number) parts.push(`UN-${dg.un_number}`);
    if (dg.proper_shipping_name) parts.push(dg.proper_shipping_name);
    if (dg.packing_instruction) parts.push(`${dg.packing_instruction}`);
    if (dg.nos_description) {
      parts.push(`N.O.S. (${dg.nos_description})`);
    }
    if (dg.hazard_class) parts.push(dg.hazard_class);
    if (dg.packing_group) parts.push(dg.packing_group);
    if (dg.tunnel_restriction_code) {
      parts.push(`${dg.tunnel_restriction_code}`);
    }
    if (dg.quantity_value) parts.push(`${dg.quantity_value} ${dg.quantity_unit || "?"}`);
    if (dg.environmentally_hazardous) {
      parts.push(t("order_lines.environmental_hazardous"));
    }

    return parts.join(", ");
  };

  const fetchDangerousGoodsTemplates = async () => {
    if (dangerousGoodsTemplates.value.length > 0) return;
    const response = await homeFetch("dangerous-goods-templates");
    const data = (await response.data) as DangerousGoodsTemplate[];
    dangerousGoodsTemplates.value = data.map((t, i) => ({
      ...t,
      label: t.name || t.un_number?.toString() || String(i + 1),
      value: i,
    }));
  };

  const clearDangerousGoodsTemplates = () => {
    dangerousGoodsTemplates.value = [];
  };

  return {
    formatDangerousGoodsSummary,
    formatOrderlineTitle,
    formatOrderlineTitleConsignment,
    formatOrderlineTitleShort,
    orderLineMissingInfo,
    generateDangerousGoods,
    fetchDangerousGoodsTemplates,
    clearDangerousGoodsTemplates,
    generateOrderline,
    returnSelecteditem,
    dangerousGoodsTemplates,
    countries,
    currencies,
    units,
  };
};
